import React, { useCallback, useMemo, useState } from 'react';
import type { Editor } from '@tiptap/core';
import type { ContentEditableType } from '@bay-email/editor';

export interface ActiveEditableTarget {
	// Form field path, same value as data-content_editable-idx.
	idx: string;
	type: ContentEditableType;
}

export interface ActiveEditable extends ActiveEditableTarget {
	// Set only while the active editable is tiptap-backed.
	editor: Editor | null;
}

interface ActiveEditableContextValue {
	active: ActiveEditable | null;
	setActive: (next: ActiveEditableTarget | null) => void;
	registerEditor: (idx: string, editor: Editor | null) => void;
}

const noop = () => {};

const ActiveEditableContext = React.createContext<ActiveEditableContextValue>({
	active: null,
	setActive: noop,
	registerEditor: noop,
});

export function ActiveEditableProvider({ children }: { children?: React.ReactNode }) {
	const [target, setTarget] = useState<ActiveEditableTarget | null>(null);
	const [editors, setEditors] = useState<Record<string, Editor>>({});

	// Same idx + type must be a no-op: every click inside the editor calls this.
	const setActive = useCallback<ActiveEditableContextValue['setActive']>(next => {
		setTarget(prev => (prev?.idx === next?.idx && prev?.type === next?.type ? prev : next));
	}, []);

	const registerEditor = useCallback<ActiveEditableContextValue['registerEditor']>(
		(idx, editor) => {
			setEditors(prev => {
				if (editor) {
					return prev[idx] === editor ? prev : { ...prev, [idx]: editor };
				}
				if (!(idx in prev)) return prev;
				const { [idx]: _removed, ...rest } = prev;
				return rest;
			});
		},
		[],
	);

	const active = useMemo<ActiveEditable | null>(
		() => (target ? { ...target, editor: editors[target.idx] ?? null } : null),
		[target, editors],
	);

	const value = useMemo(
		() => ({ active, setActive, registerEditor }),
		[active, setActive, registerEditor],
	);

	return (
		<ActiveEditableContext.Provider value={value}>
			{children}
		</ActiveEditableContext.Provider>
	);
}

export function useActiveEditable() {
	return React.useContext(ActiveEditableContext);
}
